import { LinearProgress } from 'components/LinearProgress';
import { Format } from 'helpers/Format';
import { FC, useCallback, useEffect, useState } from 'react';

import LeftIcon from '@mui/icons-material/ChevronLeft';
import RightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  Collapse,
  Divider,
  Fab,
  Grid,
  IconButton,
  List,
  ListItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import { TutorService } from 'services/tutor.service';
import { ITutorList } from 'types/Tutor';
import { ITutorPlan } from 'types/TutorPlan';
import { maskCurrencyFormat, maskDate } from 'util/masks';

interface ITutorListProps extends ITutorList {
  currentPage: number;
  setPage: Function;
  maxPage: number;
  loading: boolean;
}
interface ITutorRow {
  tutor: any;
}

const TutorRow: FC<ITutorRow> = ({ tutor }) => {
  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(tutor.active);
  const [openModal, setOpenModal] = useState(false);
  const [plans, setPlans] = useState<ITutorPlan[]>([]);
  const createdAtDateObj = new Date(tutor.createdAt);
  const createdAt = `${createdAtDateObj.getDate()}/${
    createdAtDateObj.getMonth() + 1
  }/${createdAtDateObj.getFullYear()}`;

  const theme = useTheme();

  const handleClickOpen = () => {
    setOpenModal(true);
  };

  const handleDisableTutor = () => {
    TutorService.disableTutor(tutor.cpf, !tutor.active);
    setChecked(!checked);
    setOpenModal(false);
  };

  const handleClose = () => {
    setOpenModal(false);
  };

  const PlansTutor: React.FC<ITutorPlan> = ({
    plan,
    expireAt,
    treatmentsLeft,
    active,
  }) => {

    if (!plan) {
      return null;
    }

    return (
      <>
        <Card variant="outlined" style={{ width: 400, marginBottom: 4 }}>
          <Stack
            direction="column"
            justifyContent="center"
            alignItems="left"
            spacing={1}
          >
            <CardContent
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                paddingBottom: 0,
              }}
            >
              <Typography sx={{ fontSize: 18, fontWeight: 'bold' }}>
                {plan.title}
              </Typography>
              <Typography
                color={theme.palette.primary.main}
                sx={{ fontWeight: 'bold' }}
              >
                {maskCurrencyFormat(plan.price)}
              </Typography>
            </CardContent>
            <CardActions>
              <Stack direction="row" justifyContent="space-around" flex={1}>
                <Stack direction="column" justifyContent="center">
                  <Typography style={{ textAlign: 'center' }}>
                    Válido até
                  </Typography>
                  <Typography
                    color={theme.palette.primary.main}
                    style={{
                      textAlign: 'center',
                      fontWeight: 'bold',
                    }}
                  >
                    {maskDate(expireAt)}
                  </Typography>
                </Stack>

                <Divider orientation="vertical" flexItem />

                <Stack
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                >
                  <Typography>
                    Restam
                    <Box
                      component="span"
                      color={theme.palette.primary.main}
                      fontWeight="bold"
                    >
                      {' '}
                      {treatmentsLeft}
                    </Box>
                  </Typography>
                  <Typography>Teleorientações</Typography>
                </Stack>

                <Divider orientation="vertical" flexItem />

                <Box display="flex" alignItems="center" justifyContent="center">
                  <Typography
                    whiteSpace="pre-line"
                    align="center"
                    color={theme.palette.grey[500]}
                  >
                    {`Plano\n${active ? 'Ativo' : 'Cancelado'}`}
                  </Typography>
                </Box>
              </Stack>
            </CardActions>
          </Stack>
        </Card>
      </>
    );
  };

  const getPlans = useCallback(
    async (id: string) => {
      const plansTutor = await TutorService.getPlansTutor(id);
      if (plansTutor.status === 200) {
        setPlans(plansTutor.data);
      }
    },
    [setPlans]
  );

  useEffect(() => {
    getPlans(tutor._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <TableRow
        sx={{
          '&:hover, &:hover + *': { backgroundColor: '#ddd' },
        }}
      >
        <TableCell component="th" scope="row">
          {tutor.name}
        </TableCell>
        <TableCell>{Format.cpf(tutor.cpf)}</TableCell>
        <TableCell component="th" scope="row">
          {}
        </TableCell>
        <TableCell align="right">
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={() => setOpen(!open)}
          >
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell
          style={{
            paddingBottom: 0,
            paddingTop: 0,
            paddingLeft: 0,
            paddingRight: 0,
            border: 0,
          }}
          colSpan={6}
        >
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Table aria-label="more-info">
              <TableHead>
                <TableRow>
                  <TableCell padding="normal" style={{ width: 600 }}>
                    <Grid item xs={8}>
                      <Typography>
                        <strong>E-mail:</strong> {tutor.email}
                      </Typography>
                      {/* FIXME: get active plan of tutor */}
                      {/* <Typography>
                        <strong>Assinatura:</strong>{' '}
                        {tutor.active ? 'Ativada' : 'Desativada'}
                      </Typography> */}
                      <Typography>
                        <strong>Data de Cadastro:</strong> {createdAt}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <FormControl>
                        <FormControlLabel
                          value={tutor.active}
                          control={
                            <Switch
                              color="primary"
                              checked={checked}
                              onClick={handleClickOpen}
                            />
                          }
                          label={`Conta ${checked ? 'Ativada' : 'Desativada'}`}
                        />
                      </FormControl>
                    </Grid>
                  </TableCell>
                  <TableCell size="small" align="right">
                    <List>
                      {plans?.map((plan) => (
                        <ListItem key={plan._id}>
                          <PlansTutor {...plan} />
                        </ListItem>
                      ))}
                    </List>
                  </TableCell>
                </TableRow>
                <Dialog
                  open={openModal}
                  onClose={handleClose}
                  aria-labelledby="alert-dialog-title"
                  aria-describedby="alert-dialog-description"
                >
                  <DialogTitle id="alert-dialog-title">
                    {'Confirmação'}
                  </DialogTitle>
                  <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                      {`Você tem certeza que deseja ${
                        checked ? 'desativar' : 'ativar'
                      } essa conta?`}
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={handleClose}>Cancelar</Button>
                    <Button onClick={handleDisableTutor} autoFocus>
                      Confirmar
                    </Button>
                  </DialogActions>
                </Dialog>
              </TableHead>
            </Table>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export const TutorList: FC<ITutorListProps> = ({
  list,
  currentPage,
  setPage,
  maxPage,
  loading,
}) => {
  const previousPage = () => {
    if (currentPage > 1) {
      setPage(currentPage - 1);
    }
  };

  const nextPage = () => {
    if (currentPage < maxPage) {
      setPage(currentPage + 1);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [maxPage, setPage]);

  return (
    <TableContainer component={Paper}>
      {loading && <LinearProgress />}
      <Table size="small" aria-label="a dense table">
        <TableHead>
          <TableRow>
            <TableCell>
              <strong>Nome</strong>
            </TableCell>
            <TableCell>
              <strong>CPF</strong>
            </TableCell>
            <TableCell>
              <strong>Plano</strong>
            </TableCell>
            <TableCell align="right">
              <strong>Ver mais</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {list?.map((tutor) => (
            <TutorRow key={tutor.cpf} tutor={tutor} />
          ))}
          <TableRow>
            <TableCell align="right" colSpan={4}>
              <Typography>
                <Fab
                  color="primary"
                  size="small"
                  aria-label="remove"
                  disabled={currentPage <= 1}
                  onClick={previousPage}
                >
                  <LeftIcon />
                </Fab>
                <Fab
                  disabled
                  size="small"
                  aria-label="add"
                  sx={{ ml: 1, mr: 1 }}
                >
                  {currentPage}
                </Fab>
                <Fab
                  color="primary"
                  size="small"
                  aria-label="add"
                  disabled={currentPage === maxPage}
                  onClick={nextPage}
                >
                  <RightIcon />
                </Fab>
              </Typography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
};

